import type { ListingViewerRole, MakerAgent } from '@ralph/contracts'
import { UserPlus, X } from 'lucide-react'
import { useId, useState } from 'react'
import { ownerName } from '@/components/marketplace/consumer/agent-identity.ts'
import { OwnerLabel } from '@/components/marketplace/consumer/OwnerLabel.tsx'
import { Badge, Button, Input } from '@/components/ui/primitives.tsx'
import { useInviteEditor, useRemoveEditor } from '@/hooks/use-maker.ts'
import { type Messages, useT } from '@/i18n/index.ts'

/**
 * Co-editors, and the two states an invitation has.
 *
 * "Invited" is shown as a distinct state rather than folded into the list, because a name
 * that appears the moment it is typed would let an owner imply a colleague's involvement
 * before that colleague agreed to it — the implication attack AGM-026 closes by requiring
 * acceptance.
 *
 * The lead sentence is true for whoever reads it (PRD-22 F-8, PRD-26 D-19): the personal owner is
 * "you"; a manager of the owning group is one of its managers; a co-editor is told who maintains
 * it; an administrator is told who is accountable and claims no exclusivity.
 */
const leadFor = (
  strings: Messages['marketplace']['maker']['editors'],
  agent: MakerAgent,
  role: ListingViewerRole
): string => {
  if (role === 'owner') return strings.leadOwner
  if (role === 'manager' && agent.owner.kind === 'group') return strings.leadManager(agent.owner.group.name)
  if (role === 'admin') return strings.leadAdmin(ownerName(agent.owner))
  return agent.owner.kind === 'group'
    ? strings.leadOtherGroup(agent.owner.group.name)
    : strings.leadOther(agent.owner.name)
}

export const EditorsPanel = ({
  agent,
  canManage,
  role
}: {
  agent: MakerAgent
  /** Whether invitations may be sent and revoked here: its maintainers, or an administrator. */
  canManage: boolean
  role: ListingViewerRole
}) => {
  // In the body, and handed to `leadFor` rather than read by it: the helper is pure, so it
  // cannot reach for a locale of its own, and nothing here is captured at import (PRD-27 F-3).
  const strings = useT().marketplace.maker.editors
  const emailId = useId()
  const [email, setEmail] = useState('')
  const invite = useInviteEditor(agent.agentId)
  const remove = useRemoveEditor(agent.agentId)

  const submit = () => {
    const value = email.trim()
    if (value === '') return
    invite.mutate(value, { onSuccess: () => setEmail('') })
  }

  return (
    <div className="card rounded-panel px-3.5 py-3">
      <h2 className="font-sans text-[14px] font-semibold tracking-[-0.018em]">{strings.title}</h2>
      <p className="mt-1.5 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{leadFor(strings, agent, role)}</p>

      <ul className="mt-3">
        <li className="flex items-center gap-2 border-b border-rule py-2 text-[13px] last:border-0">
          <OwnerLabel owner={agent.owner} size={20} to className="min-w-0 flex-1" />
          <Badge>{strings.owner}</Badge>
        </li>
        {agent.editors.map((editor) => (
          <li
            key={editor.userId}
            className="flex items-center gap-2 border-b border-rule py-2 text-[13px] last:border-0"
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate">{editor.name}</span>
              <span className="readout block truncate text-fg-subtle">{editor.email}</span>
            </span>
            <Badge tone={editor.status === 'accepted' ? 'grounded' : 'neutral'}>
              {editor.status === 'accepted' ? strings.accepted : strings.invited}
            </Badge>
            {canManage && (
              <button
                type="button"
                aria-label={strings.remove(editor.name)}
                disabled={remove.isPending}
                onClick={() => remove.mutate(editor.userId)}
                className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-fg-subtle transition-colors duration-200 hover:bg-alert-soft hover:text-alert disabled:opacity-45"
              >
                <X size={12} strokeWidth={2.2} aria-hidden />
              </button>
            )}
          </li>
        ))}
        {agent.editors.length === 0 && <li className="pt-2 text-[12.5px] text-fg-subtle">{strings.empty}</li>}
      </ul>

      {canManage && (
        <form
          className="mt-3"
          onSubmit={(event) => {
            event.preventDefault()
            submit()
          }}
        >
          <label htmlFor={emailId} className="eyebrow mb-1.5 block text-fg-muted">
            {strings.emailLabel}
          </label>
          <div className="flex gap-2">
            <Input
              id={emailId}
              type="email"
              value={email}
              placeholder={strings.emailPlaceholder}
              onChange={(event) => setEmail(event.target.value)}
            />
            <Button
              type="submit"
              icon={<UserPlus size={13} strokeWidth={2.1} aria-hidden />}
              disabled={email.trim() === '' || invite.isPending}
            >
              {invite.isPending ? strings.inviting : strings.invite}
            </Button>
          </div>
        </form>
      )}

      {invite.isError && (
        <p role="alert" className="mt-2 text-[12.5px] text-alert">
          {strings.failed}
        </p>
      )}
    </div>
  )
}
